import { useAppDispatch, useAppSelector } from '@brands/hooks/useReduxHook';
import useRoleNavigate from '@brands/hooks/useRoleNavigate';
import { CaseType } from '@brands/services/cases/types/ICase';
import { selectAuth } from '@brands/store/selectors/auth';
import { UserRoleName } from '@brands/services/identity/types/UserProfile';
import { setForm } from '@brands/store/slices/patientFormSlice';
import React, { useEffect } from 'react';

/**
 * The patient's own entry point into the Clinical Enablement intake, reached
 * from the dashboard card. This route is the one stable URL for that entry
 * point — it immediately hands off into the shared case-creation wizard
 * (SelectPatient -> upload-image -> clinical-data -> ...), the same one
 * every other async case type (Oral Health Report, Second Opinion) uses, so
 * the wizard itself needs no Clinical-Enablement-specific pages.
 */
const PatientClinicalEnablement = (): JSX.Element | null => {
  const roleNavigate = useRoleNavigate();
  const dispatch = useAppDispatch();
  const { userInfo } = useAppSelector(selectAuth);

  // This page creates the case against the signed-in user, so it is only ever
  // correct for a patient — the role segment in the URL is addressing, not
  // authorization, and staff reach the same intake through the staff
  // case-creation flow instead.
  const allowed = userInfo?.role?.name === UserRoleName.Patient || userInfo?.role?.name === UserRoleName.Dependent;

  useEffect(() => {
    if (!allowed) {
      roleNavigate('/dashboard', { replace: true });
      return;
    }
    dispatch(setForm([{ form_question_id: 20, value: { caseType: CaseType.clinical_enablement } }]));
    roleNavigate('/select-patient', {
      replace: true,
      state: { caseType: CaseType.clinical_enablement, currentUserId: userInfo.id },
    });
  }, [allowed, dispatch, roleNavigate, userInfo.id]);

  return null;
};

export default PatientClinicalEnablement;
